import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { AdornedInput, Box, FormField, FormHelp, Input, Label, Text, Textarea } from '@zillow/constellation-10';

const propTypes = {
  // boolean
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  isRequired: PropTypes.bool,
  readOnly: PropTypes.bool,

  // string
  description: PropTypes.string,
  errorMessage: PropTypes.string,
  fieldName: PropTypes.string,
  flex: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  inputType: PropTypes.string,
  initialValue: PropTypes.string,
  label: PropTypes.string,
  leftAdornment: PropTypes.string,
  maxLength: PropTypes.string,
  placeholder: PropTypes.string,
  rightAdornment: PropTypes.string,
  textareaRows: PropTypes.string,

  // function
  clearFieldError: PropTypes.oneOfType([PropTypes.func, PropTypes.oneOf([null])]),
  onBlur: PropTypes.func,
  onChange: PropTypes.oneOfType([PropTypes.func, PropTypes.oneOf([null])]),
  onFocus: PropTypes.func,

  // node
  children: PropTypes.node,
};
const defaultProps = {
  disabled: false,
  error: false,
  isRequired: false,
  readOnly: false,
  showOptional: false,
  description: '',
  errorMessage: '',
  fieldName: '',
  flex: 'auto',
  inputType: 'input',
  initialValue: '',
  label: '',
  leftAdornment: '',
  maxLength: '524288',
  placeholder: '',
  rightAdornment: '',
  textareaRows: '6',
  clearFieldError: null,
  onBlur: () => {},
  onChange: null,
  onFocus: () => {},
  children: null,
};

const RMWInput = ({
  disabled,
  error,
  isRequired,
  readOnly,
  showOptional,
  description,
  errorMessage,
  fieldName,
  flex,
  inputType,
  initialValue,
  label,
  leftAdornment,
  maxLength,
  placeholder,
  rightAdornment,
  textareaRows,
  clearFieldError,
  onBlur,
  onChange,
  onFocus,
  children,
}) => {
  const selfRef = useRef();
  const [value, setValue] = useState(initialValue);

  const fieldNameContainsPII =
    fieldName === 'contactEmail' || fieldName === 'additionalEmail' || fieldName === 'contactName';

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    // if `clearFieldError` function `null`, then focus on `errorMessage`
    if (errorMessage && !clearFieldError) {
      selfRef.current.focus();
    }
  }, [errorMessage]);

  const handleOnChange = (e) => {
    setValue(e.target.value);

    if (onChange) {
      onChange(e);
    }

    if (errorMessage && clearFieldError) {
      clearFieldError({ target: { name: fieldName } });
    }
  };

  const handleOnBlur = () => {
    if (fieldName) {
      onBlur({ target: { name: fieldName, value } });

      return;
    }

    onBlur(value);
  };

  const handleOnFocus = () => {
    onFocus({ target: { name: fieldName, value } });
  };

  const getRootProps = () => ({
    ref: selfRef,
    disabled: readOnly || disabled,
    placeholder: placeholder,
    value: value,
    onChange: handleOnChange,
    onBlur: handleOnBlur,
    onFocus: handleOnFocus,
    maxLength: parseInt(maxLength, 10),
  });

  return (
    <Box css={{ flex: flex }}>
      {/* `FormField.disabled` overrides `control.disabled` */}
      <FormField
        error={!!errorMessage || error}
        label={
          label && (
            <Label disabled={disabled} required={isRequired && !disabled}>
              {label}
              {showOptional && (
                <>
                  {' '}
                  <Text css={{ color: '{colors.legacy.coolGray.500}' }} textStyle="body-sm">
                    (optional)
                  </Text>
                </>
              )}
            </Label>
          )
        }
        control={
          <>
            {inputType === 'input' && <Input {...getRootProps()} className={fieldNameContainsPII ? 'fs-mask' : ''} />}

            {inputType === 'adorned' && (
              <AdornedInput
                disabled={readOnly || disabled}
                startAdornment={leftAdornment && <AdornedInput.Adornment>{leftAdornment}</AdornedInput.Adornment>}
                input={<AdornedInput.Input {...getRootProps()} />}
                endAdornment={rightAdornment && <AdornedInput.Adornment>{rightAdornment}</AdornedInput.Adornment>}
              />
            )}

            {inputType === 'textarea' && (
              <Textarea rows={parseInt(textareaRows, 10)} css={{ resize: 'vertical' }} {...getRootProps()} />
            )}

            {children}
          </>
        }
        description={
          errorMessage ? (
            <FormHelp error={true}>
              <span dangerouslySetInnerHTML={{ __html: errorMessage }} />
            </FormHelp>
          ) : (
            <>{description && <FormHelp>{description}</FormHelp>}</>
          )
        }
      />
    </Box>
  );
};

RMWInput.propTypes = propTypes;
RMWInput.defaultProps = defaultProps;

export default RMWInput;
